import { ServiceSchema } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import { CarrierSchema } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum RateServiceStatus {
  Filtered = "filtered",
  Disabled = "disabled",
  Error = "error",
  Unexplained = "unexplained",
}

export enum RateServiceSource {
  Filter = "filter",
  Settings = "settings",
  Provider = "provider",
  Timeout = "timeout",
  Precheck = "precheck",
}

export enum RateServiceSeverity {
  Error = "error",
  Warning = "warning",
}

/** Which side of the rating fan-out a reason applies to; in-memory only, never persisted. */
export enum RateScope {
  Merchant = "merchant_accounts",
  Redo = "redo_accounts",
}

export enum ShipmentLane {
  Domestic = "domestic",
  International = "international",
}

/** Stable reason keys persisted in snapshot rows; only ever add members. */
export enum RateServiceCode {
  RedoFedexRestricted = "redo_fedex_restricted",
  SaturdayDeliveryRequired = "saturday_delivery_required",
  SupersededByDirectUsps = "superseded_by_direct_usps",
  Deduplicated = "deduplicated",
  PoBoxNotSupported = "po_box_not_supported",
  ApoNotSupported = "apo_not_supported",
  SmartpostDryIce = "smartpost_dry_ice",
  HazmatNotSupported = "hazmat_not_supported",
  NearZeroProviderCost = "near_zero_provider_cost",
  InvalidProviderRate = "invalid_provider_rate",
  IncotermNotSupported = "incoterm_not_supported",
  UnexpectedCarrier = "unexpected_carrier",
  CarrierAccountBlocked = "carrier_account_blocked",
  TiktokUspsNotSupported = "tiktok_usps_not_supported",
  WalmartAmazonNotSupported = "walmart_amazon_not_supported",
  LandedCostNotSupported = "landed_cost_not_supported",
  LandedCostNotGuaranteed = "landed_cost_not_guaranteed",
  DisabledInSettings = "disabled_in_settings",
  BlockedByRedo = "blocked_by_redo",
  RateSourceFailed = "rate_source_failed",
  CarrierTimeout = "carrier_timeout",
  CarrierMessage = "carrier_message",
  NoHsCodes = "no_hs_codes",
  BadAddress = "bad_address",
  MultiParcelHazmat = "multi_parcel_hazmat",
  EasypostRateLimited = "easypost_rate_limited",
  RatingFailed = "rating_failed",
}

const RateServiceRowSchema = z.object({
  carrierAccountId: z.string(),
  isMerchantAccount: z.boolean(),
  carrier: CarrierSchema,
  service: ServiceSchema.nullable(),
  status: createEnum(RateServiceStatus, { enumName: "RateServiceStatus" }),
  source: createEnum(RateServiceSource, {
    enumName: "RateServiceSource",
  }).nullable(),
  code: createEnum(RateServiceCode, { enumName: "RateServiceCode" }).nullable(),
  message: z.string().nullable(),
  severity: createEnum(RateServiceSeverity, {
    enumName: "RateServiceSeverity",
  }),
});
export type RateServiceRow = z.infer<typeof RateServiceRowSchema>;

export const RateServiceRowsSchema = z.array(RateServiceRowSchema);

export interface RateServiceSnapshot {
  fulfillmentGroupId: string;
  teamId: string;
  shipmentLane: ShipmentLane;
  precheck: { code: RateServiceCode; message: string | null } | null;
  services: RateServiceRow[];
  /** When the snapshot was written, just after its run's rates saved. */
  ratedAt: Date;
}
